import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';

const PRIORIDAD = {
  critica: { label: 'Crítica', bg: '#fde8e8', color: '#8e1010', border: '#c0392b' },
  alta:    { label: 'Alta',    bg: '#fde8e8', color: '#c0392b', border: '#e74c3c' },
  media:   { label: 'Media',   bg: '#fef3e8', color: '#b7770d', border: '#e67e22' },
  baja:    { label: 'Baja',    bg: '#eafaf1', color: '#1a7a3c', border: '#27ae60' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

function HistorialCard({ s }) {
  const pr   = (s.Prioridad || 'media').toLowerCase();
  const prCfg = PRIORIDAD[pr] || PRIORIDAD.media;
  const tipo  = s.Des_Tipo_Servicio || s.Des_Clase_Servicio || '—';
  const clase = s.Des_Tipo_Servicio && s.Des_Clase_Servicio ? s.Des_Clase_Servicio : null;

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      marginBottom: 12,
      borderLeft: `4px solid ${prCfg.border}`,
      boxShadow: '0 2px 8px rgba(0,0,0,.06)',
      overflow: 'hidden',
    }}>
      {/* Cabecera */}
      <div style={{ padding: '14px 18px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Tipo + Clase */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: '#1a1a2e' }}>{tipo}</span>
            {clase && (
              <span style={{
                fontSize: 11, fontWeight: 700, borderRadius: 20,
                padding: '2px 9px', background: '#e8f0fe', color: '#1a56db',
                border: '1px solid #c7d9fa',
              }}>
                {clase}
              </span>
            )}
          </div>
          {/* Dirección */}
          <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 2 }}>
            📍 {s.Direccion || '—'}
          </div>
          {/* Fecha */}
          <div style={{ fontSize: 11, color: '#adb5bd' }}>🕐 {fmt(s.Fecha_Servicio || s.createdAt)}</div>
        </div>

        {/* Badges */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 }}>
          <span style={{
            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: prCfg.bg, color: prCfg.color, border: `1px solid ${prCfg.border}40`,
          }}>
            {prCfg.label}
          </span>
          <span style={{
            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: '#f0fdf4', color: '#15803d', border: '1px solid #86efac80',
          }}>
            ✅ Cerrado
          </span>
        </div>
      </div>

      {/* Detalles */}
      <div style={{
        borderTop: '1px solid #f1f3f5',
        padding: '10px 18px 14px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '6px 16px',
      }}>
        {s.Nombre_Solicitante && (
          <div style={{ fontSize: 12, color: '#495057' }}>
            👤 <strong>Solicitante:</strong> {s.Nombre_Solicitante}
          </div>
        )}
        {s.Unidades && (
          <div style={{ fontSize: 12, color: '#495057' }}>
            🚒 <strong>Unidades:</strong> {s.Unidades}
          </div>
        )}
        {s.Area && (
          <div style={{ fontSize: 12, color: '#495057' }}>
            🗺️ <strong>Área:</strong> {s.Area}
          </div>
        )}
        {s.NoControl && (
          <div style={{ fontSize: 12, color: '#495057' }}>
            🔢 <strong>No. Control:</strong> {s.NoControl}
          </div>
        )}
        {s.Usuario_Registra && (
          <div style={{ fontSize: 12, color: '#495057' }}>
            🖊️ <strong>Registró:</strong> {s.Usuario_Registra}
          </div>
        )}
        {s.Observaciones && (
          <div style={{ fontSize: 12, color: '#6c757d', gridColumn: '1 / -1', marginTop: 4 }}>
            📋 <em>{s.Observaciones}</em>
          </div>
        )}
      </div>
    </div>
  );
}

HistorialCard.propTypes = {
  s: PropTypes.shape({
    id:                 PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Prioridad:          PropTypes.string,
    Des_Tipo_Servicio:  PropTypes.string,
    Des_Clase_Servicio: PropTypes.string,
    Direccion:          PropTypes.string,
    Fecha_Servicio:     PropTypes.string,
    createdAt:          PropTypes.string,
    Nombre_Solicitante: PropTypes.string,
    Unidades:           PropTypes.string,
    Area:               PropTypes.string,
    NoControl:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Usuario_Registra:   PropTypes.string,
    Observaciones:      PropTypes.string,
  }).isRequired,
};

export default function Historial() {
  const [servicios, setServicios] = useState([]);
  const [cargando,  setCargando]  = useState(true);
  const [error,     setError]     = useState('');
  const [busqueda,  setBusqueda]  = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const { data } = await api.get('/servicios?estado=cerrado');
      setServicios(data || []);
    } catch (e) {
      setError('No se pudo cargar el historial.');
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = servicios.filter(s => {
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    return (
      (s.Des_Tipo_Servicio || '').toLowerCase().includes(q) ||
      (s.Des_Clase_Servicio || '').toLowerCase().includes(q) ||
      (s.Direccion || '').toLowerCase().includes(q) ||
      (s.Observaciones || '').toLowerCase().includes(q) ||
      (s.Nombre_Solicitante || '').toLowerCase().includes(q) ||
      String(s.NoControl || '').includes(q)
    );
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) { .hist-grid { padding: 12px !important; } }
      `}</style>

      <div className="hist-grid" style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
              📋 Historial de Incidentes
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6c757d' }}>
              Incidentes finalizados — {servicios.length} registros
            </p>
          </div>
          <button
            onClick={cargar}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#1a56db', color: '#fff', border: 'none',
              borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 3px 10px rgba(26,86,219,.3)',
            }}
          >
            🔄 Actualizar
          </button>
        </div>

        {/* Buscador */}
        <div style={{ marginBottom: 18 }}>
          <input
            type="text"
            placeholder="🔍  Buscar por tipo, dirección, solicitante, No. Control…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 16px', borderRadius: 10, fontSize: 13,
              border: '1.5px solid #dee2e6', outline: 'none', background: '#fff',
              boxShadow: '0 1px 4px rgba(0,0,0,.05)',
            }}
          />
        </div>

        {/* Contenido */}
        {cargando ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6c757d' }}>
            <div style={{
              width: 36, height: 36, border: '3px solid #dee2e6',
              borderTopColor: '#1a56db', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
            }} />
            Cargando historial…
          </div>
        ) : error ? (
          <div style={{
            background: '#fde8e8', border: '1px solid #fca5a5',
            borderRadius: 12, padding: '20px', color: '#b91c1c', textAlign: 'center', fontSize: 13,
          }}>
            ⚠️ {error}
          </div>
        ) : filtrados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#adb5bd' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            {busqueda ? 'Sin resultados para esa búsqueda.' : 'No hay incidentes cerrados aún.'}
          </div>
        ) : (
          filtrados.map(s => <HistorialCard key={s.id} s={s} />)
        )}
      </div>
    </div>
  );
}

